/**
 * Wohnungen – Isometrie, Filterleiste, Liste.
 *
 * Markup: page-templates/page-wohnung.php
 * Styles: src/styles/components/_wohnungen.scss
 *
 * Isometrie-SVG: Gruppen g[id^="Haus_"] > g[id^="Etage_"] > g[id^="W_"],
 * Deko in g[id^="Linien_"], g[id^="Nummer_"], g#Kompass.
 * Die W_-IDs entsprechen 'W_' + Wohnungs-Nr (z.B. W_A-0-1).
 */

const MOBILE_MAX = 760;
const SHAPES = 'path,polyline,polygon,rect,circle,ellipse,line';

// Interaktionsparameter aus dem Design.
const LIFT = 60; // px, um die Etagen darueber angehoben werden
const DUR = 600; // ms Lift-Dauer (Desktop)
const STAG = 70; // ms Versatz beim Absenken
const TOL = 60; // px Toleranz, bevor der Hover verloren geht
const DWELL = 80; // ms Verweildauer bevor aktiviert wird
const FADE = 500; // ms Farb-Uebergang

// Ruhefarbe -> Aktivfarbe der gehoverten Wohnung.
const ACTIVE_FILL = {
    '#dde4da': '#a6b39f',
    '#bfc5bd': '#8f9b89',
    '#989d96': '#616a5d',
    '#848982': '#616a5d',
};

// Ruhefarbe -> ausgegraute Farbe fuer herausgefilterte Wohnungen.
const LIGHTEN_FILL = {
    '#bfc5bd': '#d8dbd7',
    '#989d96': '#b4b9b2',
    '#848982': '#a8ada6',
    '#707a6b': '#9aa394',
};

const FILTER_KEYS = ['haus', 'zimmer', 'etage', 'frei'];

function toHex(value) {
    const raw = String(value || '').trim().toLowerCase();

    const rgb = raw.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (rgb) {
        return (
            '#' +
            [rgb[1], rgb[2], rgb[3]]
                .map((n) => Number(n).toString(16).padStart(2, '0'))
                .join('')
        );
    }

    // #abc -> #aabbcc
    if (/^#[0-9a-f]{3}$/.test(raw)) {
        return '#' + raw[1] + raw[1] + raw[2] + raw[2] + raw[3] + raw[3];
    }

    return raw;
}

/**
 * Ruhefarbe einer SVG-Form. Das Illustrator-Export nutzt `fill`-Attribute;
 * getComputedStyle ist nur Fallback (z.B. wenn per CSS eingefaerbt wird).
 *
 * @param {Element} shape SVG shape.
 * @return {string} Hex-Farbe.
 */
function restFill(shape) {
    const attr = shape.getAttribute('fill');
    if (attr && attr !== 'none' && attr !== 'currentColor') {
        return toHex(attr);
    }
    return toHex(getComputedStyle(shape).fill);
}

function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
}

class Wohnungen {
    constructor(root) {
        this.root = root;
        this.iso = root.querySelector('.js-wg-iso');
        this.sticky = root.querySelector('.js-wg-sticky');
        this.list = root.querySelector('.js-wg-list');
        this.frame = root.querySelector('.js-wg-frame');
        this.empty = root.querySelector('.js-wg-empty');
        this.header = document.getElementById('master');
        this.sheet = document.querySelector('.js-wg-sheet');
        this.sheetPanel = this.sheet && this.sheet.querySelector('.js-wg-sheet-panel');

        this.filters = { haus: [], zimmer: [], etage: [], frei: [] };
        this.activeId = null;
        this.locked = false;

        this.rest = new WeakMap();
        this.painted = [];
        this.liftedFrom = null;
        this.pending = null;
        this.byId = {};
        this.levels = [];
        this.flats = [];
        this.deco = [];

        this.rows = Array.from(root.querySelectorAll('.js-wg-row')).map((el) => ({
            el,
            id: el.dataset.id || '',
            haus: el.dataset.haus || '',
            etage: el.dataset.etage || '',
            zimmer: el.dataset.zimmer || '',
            status: el.dataset.status || 'frei',
        }));
        this.total = this.rows.length;

        this.bindRows();
        this.bindFilters();
        this.bindSheet();
        this.bindViewport();
        this.loadIso();
        this.applyFilters(false);
    }

    /* ----------------------------------------------------------- isometrie */

    loadIso() {
        if (!this.iso) {
            return;
        }
        const url = this.iso.dataset.iso;
        if (!url) {
            return;
        }
        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.status);
                }
                return response.text();
            })
            .then((text) => this.mountIso(text))
            .catch((error) => {
                this.iso.innerHTML =
                    '<div class="wohnungen__iso-error">Isometrie konnte nicht geladen werden.</div>';
                // eslint-disable-next-line no-console
                console.error('[wohnungen] iso', error);
            });
    }

    mountIso(markup) {
        this.iso.innerHTML = markup;
        const svg = this.iso.querySelector('svg');
        if (!svg) {
            return;
        }
        svg.removeAttribute('width');
        svg.removeAttribute('height');

        const dur = isMobile() ? Math.min(DUR, 250) : DUR;
        const fade = isMobile() ? Math.min(FADE, 150) : FADE;

        this.deco = Array.from(
            svg.querySelectorAll('g[id^="Linien_"],g[id^="Nummer_"],g[id^="Kompass"]')
        );
        this.levels = Array.from(svg.querySelectorAll('g[id^="Etage_"]'));
        this.flats = Array.from(svg.querySelectorAll('g[id^="W_"]'));

        this.deco.forEach((group) => {
            group.style.pointerEvents = 'none';
        });

        this.levels.forEach((level) => {
            level.style.transition = `transform ${dur}ms ease`;
            level.style.transitionDelay = '0ms';
        });

        this.flats.forEach((flat) => {
            this.byId[flat.id] = flat;
            flat.style.transition = 'opacity .3s ease, filter .3s ease';
            flat.querySelectorAll(SHAPES).forEach((shape) => {
                this.rest.set(shape, restFill(shape));
                shape.style.transition = `fill ${fade}ms ease`;
            });
            flat.addEventListener('click', (event) => {
                if (isMobile()) {
                    return;
                }
                event.stopPropagation();
                if (this.locked) {
                    this.unlock();
                    return;
                }
                if (!this.isVisibleId(flat.id)) {
                    return;
                }
                this.activate(flat.id, false);
                this.locked = true;
                this.scrollToRow(flat.id, true);
            });
        });

        this.iso.addEventListener('mousemove', (event) => {
            if (!isMobile() && !this.locked) {
                this.onIsoMove(event);
            }
        });
        this.iso.addEventListener('mouseleave', () => {
            if (!isMobile() && !this.locked) {
                this.clear();
            }
        });

        this.sizeIso();
        this.applyFilterToSvg();
        this.onScroll();
    }

    sizeIso() {
        const svg = this.iso && this.iso.querySelector('svg');
        if (!svg) {
            return;
        }
        const width = this.iso.clientWidth || window.innerWidth;
        if (this.isoWidth === width && this.isoMobile === isMobile()) {
            return;
        }
        this.isoWidth = width;
        this.isoMobile = isMobile();

        const box = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
        const ratio = box.length === 4 && box[2] > 0 && box[3] > 0 ? box[3] / box[2] : 293.4 / 903.2;

        const shell = document.getElementById('root');
        const shellH = isMobile() && shell ? shell.clientHeight : window.innerHeight;
        let maxH = shellH * (isMobile() ? 0.28 : 0.38);

        if (isMobile()) {
            const rest = shellH - 64 - 64 - 24;
            if (rest - maxH < shellH * 0.4) {
                maxH = Math.max(80, rest - shellH * 0.4);
            }
        }

        let w = width;
        let h = w * ratio;
        if (h > maxH) {
            h = maxH;
            w = h / ratio;
        }
        svg.style.width = `${Math.round(w)}px`;
        svg.style.height = `${Math.round(h)}px`;
    }

    /* ---------------------------------------------------------- hover/lift */

    hitAt(x, y) {
        const el = document.elementFromPoint(x, y);
        return el && el.closest ? el.closest('g[id^="W_"]') : null;
    }

    distToRect(x, y, rect) {
        const dx = Math.max(rect.left - x, 0, x - rect.right);
        const dy = Math.max(rect.top - y, 0, y - rect.bottom);
        return Math.hypot(dx, dy);
    }

    cancelPending() {
        if (this.pending) {
            clearTimeout(this.pending.timer);
            this.pending = null;
        }
    }

    schedule(id) {
        if (this.pending && this.pending.id === id) {
            return;
        }
        this.cancelPending();
        this.pending = {
            id,
            timer: setTimeout(() => {
                this.pending = null;
                this.activate(id, true);
            }, DWELL),
        };
    }

    onIsoMove(event) {
        const flat = this.hitAt(event.clientX, event.clientY);
        if (flat) {
            if (!this.isVisibleId(flat.id)) {
                return;
            }
            if (flat.id === this.activeId) {
                this.cancelPending();
                this.lift(flat.parentElement);
                return;
            }
            this.schedule(flat.id);
            return;
        }
        if (!this.activeId) {
            return;
        }
        const current = this.byId[this.activeId];
        if (!current) {
            return;
        }
        if (this.distToRect(event.clientX, event.clientY, current.getBoundingClientRect()) > TOL) {
            this.clear();
        }
    }

    unpaint() {
        this.painted.forEach((shape) => {
            shape.style.fill = '';
        });
        this.painted = [];
    }

    unlift() {
        if (!this.liftedFrom) {
            return;
        }
        let sibling = this.liftedFrom.nextElementSibling;
        let index = 0;
        while (sibling) {
            if (/^Etage_/.test(sibling.id)) {
                sibling.style.transitionDelay = `${index * STAG}ms`;
                index += 1;
            }
            sibling = sibling.nextElementSibling;
        }
        this.liftedFrom = null;
        this.levels.forEach((level) => {
            level.style.transform = '';
        });
    }

    lift(level) {
        if (!level || this.liftedFrom === level) {
            return;
        }
        this.unlift();
        this.liftedFrom = level;
        let sibling = level.nextElementSibling;
        while (sibling) {
            if (/^Etage_/.test(sibling.id)) {
                sibling.style.transitionDelay = '0ms';
                sibling.style.transform = `translateY(${-LIFT}px)`;
            }
            sibling = sibling.nextElementSibling;
        }
    }

    unlock() {
        this.locked = false;
        this.clear();
    }

    clear() {
        clearTimeout(this.rowTimer);
        this.cancelPending();
        this.unpaint();
        this.unlift();
        this.setActive(null);
    }

    activate(id, fromSvg) {
        this.cancelPending();
        this.unpaint();

        const flat = this.byId[id];
        if (flat) {
            flat.querySelectorAll(SHAPES).forEach((shape) => {
                const from = this.rest.get(shape) || restFill(shape);
                const to = ACTIVE_FILL[from];
                if (to) {
                    shape.style.fill = to;
                    this.painted.push(shape);
                }
            });
            // Der Etagen-Lift (Etagen darüber anheben) gehört wie im Entwurf
            // auch auf Mobile dazu. Er ist in SVG-viewBox-Einheiten definiert,
            // also proportional und damit auch auf der kleinen Iso sicher.
            this.lift(flat.parentElement);
        }

        this.setActive(id);

        if (fromSvg) {
            clearTimeout(this.rowTimer);
            this.rowTimer = setTimeout(() => {
                if (this.activeId === id) {
                    this.scrollToRow(id, false);
                }
            }, 500);
        }
    }

    setActive(id) {
        if (this.activeId === id) {
            return;
        }
        this.activeId = id;
        this.rows.forEach((row) => {
            row.el.classList.toggle('is-active', !!id && row.id === id);
        });
    }

    /* ------------------------------------------------------------- filters */

    isVisible(row) {
        const f = this.filters;
        return (
            (!f.haus.length || f.haus.indexOf(row.haus) !== -1) &&
            (!f.zimmer.length || f.zimmer.indexOf(row.zimmer) !== -1) &&
            (!f.etage.length || f.etage.indexOf(row.etage) !== -1) &&
            (!f.frei.length || row.status === 'frei')
        );
    }

    isVisibleId(id) {
        const row = this.rows.find((item) => item.id === id);
        return !row || this.isVisible(row);
    }

    bindFilters() {
        this.root.querySelectorAll('.js-wg-seg-btn').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                const seg = button.closest('.js-wg-seg');
                const open = seg.classList.contains('is-open');
                this.closeMenus();
                if (!open) {
                    this.openMenu(seg);
                }
            });
        });

        document.querySelectorAll('.js-wg-opt').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                const { key, value } = button.dataset;
                if (FILTER_KEYS.indexOf(key) === -1) {
                    return;
                }
                const list = this.filters[key];
                const at = list.indexOf(value);
                if (at === -1) {
                    list.push(value);
                } else {
                    list.splice(at, 1);
                }
                this.applyFilters(true);
            });
        });

        document.querySelectorAll('.js-wg-reset').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                FILTER_KEYS.forEach((key) => {
                    this.filters[key] = [];
                });
                this.closeMenus();
                this.applyFilters(true);
            });
        });

        document.addEventListener('click', (event) => {
            if (!event.target.closest('.js-wg-seg')) {
                this.closeMenus();
            }
            if (this.locked && !event.target.closest('a') && !event.target.closest('.js-wg-row')) {
                this.unlock();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            this.closeMenus();
            this.closeSheet();
            if (this.locked) {
                this.unlock();
            }
        });
    }

    openMenu(seg) {
        seg.classList.add('is-open');
        const button = seg.querySelector('.js-wg-seg-btn');
        const menu = seg.querySelector('.js-wg-menu');
        if (button) {
            button.setAttribute('aria-expanded', 'true');
        }
        if (menu) {
            menu.hidden = false;
            const rect = menu.getBoundingClientRect();
            seg.classList.toggle('wohnungen__fseg--last', rect.right > window.innerWidth - 8);
        }
    }

    closeMenus() {
        this.root.querySelectorAll('.js-wg-seg.is-open').forEach((seg) => {
            seg.classList.remove('is-open');
            const button = seg.querySelector('.js-wg-seg-btn');
            const menu = seg.querySelector('.js-wg-menu');
            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }
            if (menu) {
                menu.hidden = true;
            }
        });
    }

    applyFilters(userAction) {
        let shown = 0;
        this.rows.forEach((row) => {
            const visible = this.isVisible(row);
            row.el.hidden = !visible;
            if (visible) {
                shown += 1;
            }
        });

        document.querySelectorAll('.js-wg-opt').forEach((button) => {
            const { key, value } = button.dataset;
            const on = FILTER_KEYS.indexOf(key) !== -1 && this.filters[key].indexOf(value) !== -1;
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        this.root.querySelectorAll('.js-wg-seg').forEach((seg) => {
            const key = seg.dataset.key;
            const value = seg.querySelector('.js-wg-seg-value');
            if (!value) {
                return;
            }
            const chosen = Array.from(seg.querySelectorAll('.js-wg-opt'))
                .filter((button) => this.filters[key].indexOf(button.dataset.value) !== -1)
                .map((button) => button.dataset.short);
            value.textContent = chosen.length ? chosen.join(', ') : value.dataset.default;
        });

        document.querySelectorAll('.js-wg-count').forEach((el) => {
            el.textContent = `${shown} von ${this.total} Wohnungen`;
        });
        document.querySelectorAll('.js-wg-count-short').forEach((el) => {
            el.textContent = `${shown} von ${this.total}`;
        });

        if (this.empty) {
            this.empty.hidden = shown !== 0;
        }

        this.sizeFrame();
        this.applyFilterToSvg();

        if (userAction) {
            this.afterFilterChange();
        }
    }

    applyFilterToSvg() {
        if (!this.flats.length) {
            return;
        }
        const visible = {};
        this.rows.forEach((row) => {
            if (row.id) {
                visible[row.id] = this.isVisible(row);
            }
        });

        this.flats.forEach((flat) => {
            const off = visible[flat.id] === false;
            flat.style.opacity = off ? '.75' : '';
            flat.style.filter = off ? 'grayscale(1) brightness(1.08)' : '';
            flat.querySelectorAll(SHAPES).forEach((shape) => {
                const light = LIGHTEN_FILL[this.rest.get(shape)];
                if (light && this.painted.indexOf(shape) === -1) {
                    shape.style.fill = off ? light : '';
                }
            });
        });

        this.deco.forEach((group) => {
            const level = group.closest('g[id^="Etage_"]');
            if (!level) {
                return;
            }
            const anyVisible = Array.from(level.querySelectorAll('g[id^="W_"]')).some(
                (flat) => visible[flat.id] !== false
            );
            group.style.transition = 'opacity .3s ease, filter .3s ease';
            group.style.opacity = anyVisible ? '' : '.75';
            group.style.filter = anyVisible ? '' : 'grayscale(1) brightness(1.08)';
            group.querySelectorAll(SHAPES).forEach((shape) => {
                if (!this.rest.has(shape)) {
                    this.rest.set(shape, restFill(shape));
                }
                const light = LIGHTEN_FILL[this.rest.get(shape)];
                if (light) {
                    shape.style.fill = anyVisible ? '' : light;
                }
            });
        });
    }

    afterFilterChange() {
        this.locked = false;
        this.cancelPending();
        this.unpaint();
        this.unlift();
        this.setActive(null);

        const panel = this.panel();
        if (panel) {
            panel.scrollTo({ top: 0, behavior: 'instant' });
            this.onScroll();
            return;
        }
        if (!this.list || !this.sticky) {
            return;
        }
        requestAnimationFrame(() => {
            const first = this.list.querySelector('.js-wg-row:not([hidden])');
            if (!first) {
                return;
            }
            const edge = (this.header ? this.header.offsetHeight : 0) + this.sticky.offsetHeight;
            const firstDoc = first.getBoundingClientRect().top + window.scrollY;
            const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            const target = Math.min(maxY, Math.max(0, firstDoc - edge));
            if (target < window.scrollY) {
                this.smoothScrollBy(target - window.scrollY);
            }
        });
    }

    /* ---------------------------------------------------------------- rows */

    bindRows() {
        this.rows.forEach((row) => {
            row.el.addEventListener('mouseenter', () => {
                if (!isMobile() && !this.locked) {
                    this.activate(row.id, false);
                }
            });

            row.el.addEventListener('click', (event) => {
                if (event.target.closest('a')) {
                    return;
                }
                if (isMobile()) {
                    // Zeile antippen hebt die Wohnung in der Isometrie hervor;
                    // nochmaliges Antippen derselben Zeile hebt sie wieder auf.
                    if (this.activeId === row.id) {
                        this.clear();
                    } else {
                        this.activate(row.id, false);
                        this.scrollToRow(row.id, false);
                    }
                    return;
                }
                event.stopPropagation();
                if (this.locked && this.activeId === row.id) {
                    this.unlock();
                    return;
                }
                this.activate(row.id, false);
                this.locked = true;
            });
        });

        if (this.list) {
            this.list.addEventListener('mouseleave', () => {
                if (!isMobile() && !this.locked) {
                    this.clear();
                }
            });
        }
    }

    panel() {
        return isMobile() && this.list && this.list.scrollHeight > this.list.clientHeight + 1
            ? this.list
            : null;
    }

    /**
     * Viewport-Oberkante des grünen Rahmens: direkt unter Header und
     * Isometrie/Filter (beide kleben oben).
     *
     * @return {number} px
     */
    frameTop() {
        const headerH = this.header ? this.header.offsetHeight : 0;
        return headerH + (this.sticky ? this.sticky.offsetHeight : 0);
    }

    onScroll() {
        if (!isMobile() || !this.list) {
            return;
        }
        if (this.frame && !this.panel()) {
            // Letzte Zeile hat den Rahmen verlassen: Rahmen ausblenden und
            // Snapping aus, sonst zieht die Seite zurück zur letzten Zeile.
            const listBottom = this.list.getBoundingClientRect().bottom;
            const past = listBottom < this.frameTop() + 56 - 2;
            this.frame.classList.toggle('is-off', past);
            document.documentElement.classList.toggle('wg-nosnap', past);
        }
        const edge = this.panel()
            ? this.list.getBoundingClientRect().top + 30
            : this.frameTop() + 30;
        let best = null;
        let bestDistance = Infinity;
        this.rows.forEach((row) => {
            if (row.el.hidden) {
                return;
            }
            const rect = row.el.getBoundingClientRect();
            const distance =
                rect.top <= edge && rect.bottom >= edge
                    ? 0
                    : Math.min(Math.abs(rect.top - edge), Math.abs(rect.bottom - edge));
            if (distance < bestDistance) {
                bestDistance = distance;
                best = row;
            }
        });
        if (best && best.id !== this.activeId) {
            this.activate(best.id, false);
        }
    }

    smoothScrollBy(delta) {
        if (Math.abs(delta) < 1) {
            return;
        }
        if (this.anim) {
            cancelAnimationFrame(this.anim);
        }
        const panel = this.panel();
        const start = panel ? panel.scrollTop : window.scrollY;
        const startedAt = performance.now();
        const duration = Math.min(1400, 600 + Math.abs(delta) * 0.6);
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        // `behavior: 'instant'` ist wichtig: das Theme setzt global
        // `html { scroll-behavior: smooth }`. Ohne das Override wuerde jeder
        // Frame dieser Animation nochmals weich gescrollt – das Ergebnis
        // ruckelt bzw. bleibt stehen.
        const step = (now) => {
            const t = Math.min(1, (now - startedAt) / duration);
            const top = start + delta * ease(t);
            (panel || window).scrollTo({ top, left: 0, behavior: 'instant' });
            this.anim = t < 1 ? requestAnimationFrame(step) : null;
        };
        this.anim = requestAnimationFrame(step);
    }

    scrollToRow(id, center) {
        if (!this.list || !this.sticky) {
            return;
        }
        const row = this.list.querySelector(`.js-wg-row[data-id="${id}"]`);
        if (!row) {
            return;
        }
        const panel = this.panel();
        if (panel) {
            panel.scrollTo({ top: row.offsetTop - panel.offsetTop, behavior: 'smooth' });
            return;
        }
        if (isMobile()) {
            // Zeile in den grünen Rahmen schieben; nativ, damit Scroll-Snap
            // der Seite nicht gegen eine JS-Animation arbeitet.
            const target = row.getBoundingClientRect().top + window.scrollY - this.frameTop();
            window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
            return;
        }
        const rowRect = row.getBoundingClientRect();
        const stickyRect = this.sticky.getBoundingClientRect();
        const top = stickyRect.bottom;
        const bottom = window.innerHeight;
        if (center) {
            this.smoothScrollBy(rowRect.top - top - (bottom - top) / 2 + rowRect.height / 2);
            return;
        }
        if (rowRect.top < top) {
            this.smoothScrollBy(rowRect.top - top - 8);
        } else if (rowRect.bottom > bottom) {
            this.smoothScrollBy(rowRect.bottom - bottom + 8);
        }
    }

    /* --------------------------------------------------------------- sheet */

    bindSheet() {
        if (!this.sheet) {
            return;
        }
        this.root.querySelectorAll('.js-wg-sheet-open').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                this.openSheet();
            });
        });
        this.sheet.querySelectorAll('.js-wg-sheet-close').forEach((button) => {
            button.addEventListener('click', () => this.closeSheet());
        });

        const handle = this.sheet.querySelector('.js-wg-sheet-drag');
        if (handle && this.sheetPanel) {
            let startY = null;
            let delta = 0;
            handle.addEventListener(
                'touchstart',
                (event) => {
                    startY = event.touches[0].clientY;
                    this.sheetPanel.style.transition = 'none';
                },
                { passive: true }
            );
            handle.addEventListener(
                'touchmove',
                (event) => {
                    if (startY === null) {
                        return;
                    }
                    delta = Math.max(0, event.touches[0].clientY - startY);
                    this.sheetPanel.style.transform = `translateY(${delta}px)`;
                },
                { passive: true }
            );
            handle.addEventListener('touchend', () => {
                startY = null;
                this.sheetPanel.style.transition = '';
                if (delta > 80) {
                    this.closeSheet();
                } else {
                    this.sheetPanel.style.transform = '';
                }
                delta = 0;
            });
        }
    }

    openSheet() {
        if (!this.sheet) {
            return;
        }
        this.closeMenus();
        this.sheet.hidden = false;
        requestAnimationFrame(() => this.sheet.classList.add('is-open'));
        document.body.classList.add('wohnungen-sheet-open');
        this.root.querySelectorAll('.js-wg-sheet-open').forEach((button) => {
            button.setAttribute('aria-expanded', 'true');
        });
        const focusable = this.sheet.querySelector('.js-wg-sheet-focus');
        if (focusable) {
            focusable.focus();
        }
    }

    closeSheet() {
        if (!this.sheet || this.sheet.hidden) {
            return;
        }
        this.sheet.classList.remove('is-open');
        document.body.classList.remove('wohnungen-sheet-open');
        this.root.querySelectorAll('.js-wg-sheet-open').forEach((button) => {
            button.setAttribute('aria-expanded', 'false');
        });
        if (this.sheetPanel) {
            this.sheetPanel.style.transform = '';
        }
        setTimeout(() => {
            this.sheet.hidden = true;
        }, 250);
    }

    /* ------------------------------------------------------------ viewport */

    measure() {
        if (!this.sticky) {
            return;
        }
        const headerH = this.header ? this.header.offsetHeight : 0;
        const snap = headerH + this.sticky.offsetHeight + 6;
        this.root.style.setProperty('--wg-snap', `${snap}px`);
        this.root.style.setProperty('--wg-frame-top', `${this.frameTop()}px`);

        if (this.list && isMobile() && this.panel()) {
            this.list.style.setProperty(
                '--wg-tail',
                `${Math.max(0, this.list.clientHeight - 56 - 24)}px`
            );
        } else if (this.list) {
            this.list.style.removeProperty('--wg-tail');
        }

        this.sizeFrame();
    }

    /**
     * Statischer grüner Rahmen exakt so breit wie eine Mini-Card (Scrollbar
     * ausgenommen), damit er nicht breiter wirkt als die Zeilen darunter.
     * Sichtbar ist der Rahmen nur im mobilen App-Layout (CSS); ohne sichtbare
     * Zeile wird er ausgeblendet.
     */
    sizeFrame() {
        if (!this.frame) {
            return;
        }
        if (!isMobile() || !this.list) {
            this.frame.style.width = '';
            return;
        }
        this.frame.style.width = `${this.list.clientWidth}px`;
        const anyVisible = this.rows.some((row) => !row.el.hidden);
        this.frame.hidden = !anyVisible;
    }

    bindViewport() {
        const onScroll = () => {
            if (this.scrollRaf) {
                return;
            }
            this.scrollRaf = requestAnimationFrame(() => {
                this.scrollRaf = null;
                this.onScroll();
            });
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        if (this.list) {
            this.list.addEventListener('scroll', onScroll, { passive: true });
        }

        window.addEventListener('resize', () => {
            this.sizeIso();
            this.measure();
            onScroll();
        });

        if (window.ResizeObserver) {
            const observer = new ResizeObserver(() => this.measure());
            if (this.sticky) {
                observer.observe(this.sticky);
            }
            if (this.header) {
                observer.observe(this.header);
            }
            if (this.list) {
                observer.observe(this.list);
            }
        }

        this.measure();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('.js-wohnungen');
    if (root && !root.dataset.wgBound) {
        root.dataset.wgBound = '1';
        new Wohnungen(root);
    }
});
